import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { 
  Plus, Search, Edit2, Power, PowerOff, Image, 
  RefreshCw, CheckCircle, XCircle, Building2, Tag
} from 'lucide-react';

const BrandMaster = ({ user, onLogout }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: 'Mobile Phones',
    logo_url: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const categories = ['Mobile Phones', 'Laptops', 'Tablets', 'Accessories'];

  useEffect(() => {
    fetchBrands();
  }, [selectedCategory, showInactive]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/brands`;
      const params = new URLSearchParams();
      
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (!showInactive) {
        params.append('is_active', 'true');
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setBrands(data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brands`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create brand');
      }
      
      setShowCreateModal(false);
      setFormData({ name: '', category: 'Mobile Phones', logo_url: '' });
      fetchBrands();
    } catch (error) {
      setFormError(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateBrand = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/brands/${editingBrand.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            name: formData.name,
            logo_url: formData.logo_url
          })
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update brand');
      }
      
      setShowEditModal(false);
      setEditingBrand(null);
      setFormData({ name: '', category: 'Mobile Phones', logo_url: '' });
      fetchBrands();
    } catch (error) {
      setFormError(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (brand) => {
    try {
      const endpoint = brand.is_active 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/brands/${brand.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/brands/${brand.id}/activate`;
      
      const response = await fetch(endpoint, {
        method: brand.is_active ? 'DELETE' : 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to update brand status');
      }
      
      fetchBrands();
    } catch (error) {
      console.error('Error toggling brand status:', error);
    }
  };

  const openEditModal = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      category: brand.category,
      logo_url: brand.logo_url || ''
    });
    setFormError('');
    setShowEditModal(true);
  };

  const filteredBrands = brands.filter(brand => 
    brand.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeBrandsCount = brands.filter(b => b.is_active !== false).length;
  const inactiveBrandsCount = brands.filter(b => b.is_active === false).length;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-7 h-7 text-[#2b3380]" />
              Brand Master
            </h1>
            <p className="text-gray-600 mt-1">Manage product brands across all categories</p>
          </div>
          
          <button
            onClick={() => {
              setFormData({ name: '', category: 'Mobile Phones', logo_url: '' });
              setFormError('');
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] transition-colors font-medium"
            data-testid="create-brand-btn"
          >
            <Plus className="w-5 h-5" />
            Create Brand
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Tag className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{brands.length}</p>
                <p className="text-sm text-gray-500">Total Brands</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{activeBrandsCount}</p>
                <p className="text-sm text-gray-500">Active Brands</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{inactiveBrandsCount}</p>
                <p className="text-sm text-gray-500">Inactive Brands</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search brands..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                data-testid="brand-search-input"
              />
            </div>
            
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
              data-testid="brand-category-filter"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            
            {/* Show Inactive Toggle */}
            <label className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-4 h-4 text-[#2b3380] rounded focus:ring-[#2b3380]"
              />
              <span className="text-sm text-gray-700">Show Inactive</span>
            </label>
            
            {/* Refresh */}
            <button
              onClick={fetchBrands}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="refresh-brands-btn"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Brands Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2b3380]" />
          </div>
        ) : filteredBrands.length === 0 ? (
          <div className="bg-white rounded-xl p-12 border border-gray-100 shadow-sm text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No brands found</h3>
            <p className="text-gray-500 mb-4">Create your first brand to get started</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Brand
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredBrands.map(brand => (
              <div
                key={brand.id}
                className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${
                  brand.is_active === false ? 'border-red-200 opacity-60' : 'border-gray-100'
                }`}
                data-testid={`brand-card-${brand.id}`}
              >
                {/* Brand Logo */}
                <div className="h-32 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
                  {brand.logo_url ? (
                    <img
                      src={brand.logo_url}
                      alt={brand.name}
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-16 h-16 bg-[#2b3380] rounded-xl flex items-center justify-center text-white text-2xl font-bold ${brand.logo_url ? 'hidden' : ''}`}
                  >
                    {brand.name?.substring(0, 2).toUpperCase()}
                  </div>
                </div>
                
                {/* Brand Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">{brand.name}</h3>
                      <p className="text-sm text-gray-500">{brand.category}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      brand.is_active === false 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {brand.is_active === false ? 'Inactive' : 'Active'}
                    </span>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => openEditModal(brand)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      data-testid={`edit-brand-${brand.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(brand)}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors ${
                        brand.is_active === false
                          ? 'text-green-700 bg-green-100 hover:bg-green-200'
                          : 'text-red-700 bg-red-100 hover:bg-red-200'
                      }`}
                      data-testid={`toggle-brand-${brand.id}`}
                    >
                      {brand.is_active === false ? (
                        <>
                          <Power className="w-4 h-4" />
                          Activate
                        </>
                      ) : (
                        <>
                          <PowerOff className="w-4 h-4" />
                          Deactivate
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Brand Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900">Create New Brand</h2>
                <p className="text-sm text-gray-500 mt-1">Add a new brand to the system</p>
              </div>
              
              <form onSubmit={handleCreateBrand} className="p-6 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {formError}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                    placeholder="Enter brand name"
                    required
                    data-testid="brand-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                    required
                    data-testid="brand-category-input"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL (Optional)</label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                      placeholder="https://..."
                      data-testid="brand-logo-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate a logo</p>
                </div>
                
                {/* Logo Preview */}
                {formData.logo_url && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <img
                      src={formData.logo_url}
                      alt="Logo preview"
                      className="w-12 h-12 object-contain rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <span className="text-sm text-gray-600">Logo preview</span>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading}
                    className="flex-1 px-4 py-2.5 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="submit-create-brand"
                  >
                    {formLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Create Brand
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Brand Modal */}
        {showEditModal && editingBrand && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900">Edit Brand</h2>
                <p className="text-sm text-gray-500 mt-1">Update brand information</p>
              </div>
              
              <form onSubmit={handleUpdateBrand} className="p-6 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {formError}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                    placeholder="Enter brand name"
                    required
                    data-testid="edit-brand-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    disabled
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Category cannot be changed</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                  <input
                    type="url"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                    placeholder="https://..."
                    data-testid="edit-brand-logo-input"
                  />
                </div>
                
                {/* Logo Preview */}
                {formData.logo_url && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <img
                      src={formData.logo_url}
                      alt="Logo preview"
                      className="w-12 h-12 object-contain rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <span className="text-sm text-gray-600">Logo preview</span>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingBrand(null);
                    }}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading}
                    className="flex-1 px-4 py-2.5 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="submit-edit-brand"
                  >
                    {formLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BrandMaster;
